/**
 * Formulation 模块类型定义
 */

import type { FormulationMessage } from '@/types/formulation/formulation';

/** ORG_INFO 单条数据项 */
export interface SearchDocOrgInfo {
  entity_id?: string;
  id?: string;
  name?: string;
  name_cn?: string;
  name_en?: string;
  display_name?: string;
  normalized_name?: string;
  logo?: string;
  country_name?: string;
  website?: string;
  entity_type?: string;
}

/** search_json_docs 单条数据项 */
export interface SearchJsonDocItem {
  pbd?: string;
  apd?: string;
  title?: string;
  TITLE?: string;
  PN?: string;
  doc_id: string;
  CONTENT: string;
  doc_type: 'paper' | 'patent';
  rrf_score?: number;
  country?: string;
  ans?: string[]; // authors
  ORG_INFO?: SearchDocOrgInfo[];
}

/** 流式 AI 消息的临时 ID，用于在 conversationData 中标识未落库的流式内容 */
export const STREAMING_MESSAGE_ID = -1;

/** 流式 tool_display 的临时 ID，用于右侧面板实时展示未落库的工具结果 */
export const STREAMING_TOOL_DISPLAY_ID = -2;

// ---------------------------------------------------------------------------
// 任务状态常量：统一管理 pipeline / user-task 状态判断
// ---------------------------------------------------------------------------

/** 任务状态枚举值 */
export const TASK_STATUS = {
  RUNNING: 'running',
  STARTED: 'started',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
  TIMEOUT: 'timeout',
} as const;

/** 判断任务是否正在进行中（running 或 started） */
export function isTaskInProgress(status: string | undefined): boolean {
  return status === TASK_STATUS.RUNNING || status === TASK_STATUS.STARTED;
}

/** 判断任务是否刚启动（started） */
export function isTaskJustStarted(status: string | undefined): boolean {
  return status === TASK_STATUS.STARTED;
}

// ---------------------------------------------------------------------------
// 消息状态常量：统一管理 UI 消息展示状态
// ---------------------------------------------------------------------------

/** 消息展示状态枚举值 */
export const MESSAGE_STATUS = {
  COMPLETED: 'completed',
  EXECUTING: 'executing',
  PENDING: 'pending',
  ERROR: 'error',
} as const;

// ---------------------------------------------------------------------------
// 唯一配置入口：后续改 group 逻辑只改此处
// ---------------------------------------------------------------------------

export type FormulationGroupKind = 'intent' | 'search' | 'extract' | 'formulate';

export interface FormulationGroupConfig {
  /** 该组包含的 tool_key，用于左侧 displayBlocks 合并为一块 */
  toolKeys: string[];
  /** 该组「思考」区域只展示这些 tool_key 的 ai 消息（不填则展示该组所有 ai） */
  aiThinkingToolKeys?: string[];
  /** tool_key that acts as the divider between pre/post AI thinking messages */
  dividerToolKey?: string;
  /**
   * tool_key 用于判断该组是否「全部完成」；不填则用 divider 消息状态。
   * 例如 search 组以 search_summary 完成为准。
   */
  completionToolKey?: string;
  /**
   * 当为 true 且配置了 completionToolKey 但组内无对应消息时，降级为 divider 或最后一条消息的 status 判定。
   * 用于兼容仅含 ai_thinking（如 formulate 仅有 formulate_ai_thinking）的情况。
   */
  completionToolKeyOptional?: boolean;
  /** 工具卡片标题 i18n key 映射，优先于 Formulation.toolName.xxx */
  reportTitleI18nKeys?: Record<string, string>;
  /** 仅 search 组：参与「合并到 AI 思考」的 user_message tool_key，用于 message-utils / use-conversation */
  searchTypeKeys?: string[];
}

export const FORMULATION_GROUP_CONFIG: Record<FormulationGroupKind, FormulationGroupConfig> = {
  intent: {
    toolKeys: ['intent_ai_thinking', 'intent_summary'],
    aiThinkingToolKeys: ['intent_ai_thinking'],
    dividerToolKey: 'intent_summary',
  },
  search: {
    toolKeys: ['search_ai_thinking', 'search_summary', 'search_json_docs'],
    aiThinkingToolKeys: ['search_ai_thinking'],
    dividerToolKey: 'search_json_docs',
    completionToolKey: 'search_summary',
    searchTypeKeys: ['search_ai_thinking', 'search_summary', 'search_json_docs'],
  },
  extract: {
    toolKeys: ['extract_ai_thinking', 'extract_table_maker_json_docs', 'extract_summary'],
    aiThinkingToolKeys: ['extract_ai_thinking'],
    dividerToolKey: 'extract_table_maker_json_docs',
    completionToolKey: 'extract_summary',
    reportTitleI18nKeys: {
      extract_json_docs: 'Formulation.extractionReportTitle',
      extract_table_maker_json_docs: 'Formulation.extractionReportTitle',
    },
  },
  formulate: {
    toolKeys: ['formulate_ai_thinking', 'formulate_json_docs', 'formulate_summary'],
    aiThinkingToolKeys: ['formulate_ai_thinking'],
    dividerToolKey: 'formulate_summary',
    completionToolKey: 'formulate_summary',
    completionToolKeyOptional: true,
  },
};

/** 左侧需要显示的 tool 类型（tool 消息）；由各 group.toolKeys 并集 + ai_thinking */
export const VISIBLE_TOOL_KEYS = new Set([
  'ai_thinking',
  ...Object.values(FORMULATION_GROUP_CONFIG).flatMap((g) => g.toolKeys),
]);

/** 左侧展示时视为「搜索」内容、需合并到 AI 思考的 tool_key；由 search.searchTypeKeys 派生 */
export const SEARCH_TYPE_KEYS = new Set(FORMULATION_GROUP_CONFIG.search.searchTypeKeys ?? []);

/** AI 思考类型的 tool_key 集合；由「ai_thinking」+ 各 group.aiThinkingToolKeys 派生 */
export const AI_THINKING_TYPES = new Set([
  'ai_thinking',
  ...Object.values(FORMULATION_GROUP_CONFIG).flatMap((g) => g.aiThinkingToolKeys ?? []),
]);

/** 判断某条消息是否属于指定 group */
export function isInFormulationGroup(
  kind: FormulationGroupKind,
  toolKey: string | undefined,
): boolean {
  return FORMULATION_GROUP_CONFIG[kind].toolKeys.includes(toolKey ?? '');
}

// ---------------------------------------------------------------------------
// 左侧 ChatSection 展示块类型
// ---------------------------------------------------------------------------

/** 展示块：搜索/抽取/配方各自合并为一个块 */
export type DisplayBlock =
  | { kind: 'user'; message: FormulationMessage }
  | { kind: 'ai'; message: FormulationMessage }
  | { kind: 'ai-thinking'; message: FormulationMessage }
  | { kind: 'intent-group'; messages: FormulationMessage[] }
  | { kind: 'search-group'; messages: FormulationMessage[] }
  | { kind: 'extract-group'; messages: FormulationMessage[] }
  | { kind: 'formulate-group'; messages: FormulationMessage[] };

/** FormulationGroupKind 到 DisplayBlock group kind 的映射（模块级常量） */
export const GROUP_KIND_TO_BLOCK_KIND: Record<FormulationGroupKind, DisplayBlock['kind']> = {
  intent: 'intent-group',
  search: 'search-group',
  extract: 'extract-group',
  formulate: 'formulate-group',
};

// ---------------------------------------------------------------------------
// 对话轮次分组类型（用于按 iteration 分组展示）
// ---------------------------------------------------------------------------

/** 单轮对话：包含用户输入和 AI 响应的所有 blocks */
export interface ConversationRound {
  /** 对话轮次编号 */
  iteration: number;
  /** 用户输入 block（可选，某些轮次可能没有用户输入） */
  userBlock?: DisplayBlock;
  /** AI 响应 blocks（不包含 user 类型） */
  responseBlocks: DisplayBlock[];
}
